// import libraries
import React, { useRef } from "react";
import { useSelector } from "react-redux";
import {
  formatIncompletePhoneNumber,
  getCountryCallingCode,
  CountryCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import ReCAPTCHA from "react-google-recaptcha";
// import components
import TextInput, { ITextInputProps } from "../components/TextInput";
import DropdownSelect from "../components/DropdownSelect";
import PrimaryButton from "../components/PrimaryButton";
import {
  countrySelected,
  countryOption,
} from "../components/CountryInputRenderer";
import Tooltip from "../components/Tooltip";
import DateOfBirthField from "../components/DateOfBirthField";
import DateOfBirthFieldDesktop from "../components/DateOfBirthFieldDesktop";
import LanguageSelector from "../components/LanguageSelector";

// import utils
import { appActions, userInfoActions, ReCaptchAction } from "../actions";
import { ReducerState } from "AppTypes";
import {
  isRequired,
  checkEmail,
  onlyAlphanumeric,
  checkSize,
  isThNumber,
  validatePhoneNumber,
  validateSgNumber,
  checkShortSgId,
} from "../utils/validators";
import {
  noSymbolFilter,
  digitsOnlyFilter,
  classNames,
  retrieveLangPack,
} from "../utils/index";
import countryCodeList from "../utils/countryList";
import { addMessage } from "../components/MessageContainer";
import getStepConfig from "./stepConfig";
import api, { IApplicationResponse, ErrorResponse } from "../utils/api";
import { isDesktopWidth } from "../utils";
import { updateAATagging, updateLanguageAATagging } from "../utils/aatagging";
import { detectBrowser } from "../utils/detection";
import { dispatch } from "../store";
import "../components/_basicInfo.scss";
const BasicInfo: React.FC = () => {
  const {
    userInfo,
    isCallingApi,
    appConfig,
    lang,
    langPack,
    verifyCaptcha,
    clientSideKey,
    isCaptcha,
  } = useSelector((store: ReducerState) => ({
    userInfo: store.userInfo,
    isCallingApi: store.app.isCallingApi,
    appConfig: store.config.appConfig,
    lang: store.config.lang,
    langPack: store.config.langPack,
    verifyCaptcha: store.app.verifyCaptcha,
    clientSideKey: store.config.appConfig.clientSideKey,
    isCaptcha: store.config.appConfig.isCaptcha,
  }));
  const { isReCaptchaVerified, reCaptchaKey } = verifyCaptcha;

  const countryList = {
    SG: langPack["countrySG"],
    TH: langPack["countryTH"],
    MY: langPack["countryMY"],
    ID: langPack["countryID"],
    VN: langPack["countryVN"],
  };

  const citizenshipList = [
    {
      label:
        userInfo.activeCountry === "TH" ||
          ["VN", "ID", "MY", "CH"].includes(lang)
          ? langPack["labelCitizen"]
          : `${countryList[userInfo.activeCountry]} ${langPack["labelCitizen"]
          }`,
      value: "citizen",
    },
    {
      label: langPack["labelPermanentResident"],
      value: "pr",
    },
  ];

  const genderOptions = [
    {
      label: langPack["labelMale"],
      value: "male",
    },
    {
      label: langPack["labelFemale"],
      value: "female",
    },
  ];

  const languageList =
    appConfig.countryConfig[userInfo.activeCountry].languageOptions;

  const cluster: ITextInputProps["cluster"] = {};
  const [isTooltipOpen, setTooltipOpen] = React.useState(false);
  const [forceCountryCode, setForceCountryCode] = React.useState<{
    value: CountryCode;
  }>({
    value:
      userInfo.mobile && userInfo.activeCountry !== "TH"
        ? parsePhoneNumberFromString(userInfo.mobile).country
        : userInfo.activeCountry,
  });
  const [forceMobile, setForceMobile] = React.useState({ value: "" });
  const [isDesktopView, setDesktopView] = React.useState(false);
  const [showReCaptchaErrorMsg, setShowReCaptchaErrorMsg] =
    React.useState(false);
  const [ReCaptchaErrorMsg, setReCaptchaErrorMsg] = React.useState(
    langPack["verify_captcha"]
  );

  React.useEffect(() => {
    if (!detectBrowser()) {
      dispatch(
        appActions.gotoStep("UnsupportedBrowser", {
          title: langPack["errorDetectBrowserTitle"],
          content: langPack["errorDetectBrowserContent"],
        })
      );
    }
  }, []);

  React.useEffect(() => {
    const detect = () => setDesktopView(isDesktopWidth());
    detect();
    window.addEventListener("resize", detect);
    return () => window.removeEventListener("resize", detect);
  }, []);

  const nextStepHandler = async () => {
    let isAllValid = true;
    for (let key in cluster) {
      if (!cluster[key]()) isAllValid = false;
    }
    if (isCaptcha && !isReCaptchaVerified && !userInfo.appId) {
      setReCaptchaErrorMsg(langPack["verify_captcha"]);
      setShowReCaptchaErrorMsg(true);
      return;
    }
    if (isAllValid) {
      const reCaptchaToken = isCaptcha ? reCaptchaKey : "disabled";
      const response = !userInfo.appId
        ? await api.postInitApplication(userInfo, reCaptchaToken)
        : await api.updateApplication(userInfo);

      if (response && response.status === 200) {
        const data = response.data as IApplicationResponse;
        if (data)
          dispatch(userInfoActions.updateUserInfo({ appId: data.appId }));
        // AA Tracking
        const AALang = updateLanguageAATagging(langPack["lang"]);
        updateAATagging(
          {
            event_name: "generic_form_complete_step2",
            form_name: "UOB PoY",
            step_name: "personal_info",
            language: AALang,
            country: userInfo.activeCountry.toLowerCase(),
          },
          "other_form_step"
        );

        if (!userInfo.appId) {
          await addMessage({
            img: "images/send-email.png",
            text: `${langPack["sentEmail"]}`,
            buttons: {
              confirm: `${langPack["buttonOk"]}`,
            },
          });

          // AA Tracking
          const AALang = updateLanguageAATagging(langPack["lang"]);
          updateAATagging(
            {
              event_name: "generic_form_complete_step3",
              form_name: "UOB PoY",
              step_name: "email_send",
              language: AALang,
              country: userInfo.activeCountry.toLowerCase(),
            },
            "other_form_step"
          );
        }

        const { nextStep } = getStepConfig();
        dispatch(appActions.gotoStep(nextStep));
      } else {
        const data = response.data as ErrorResponse;
        switch (data?.errorCode) {
          case "DRAFT_ENTRY_ALREADY_EXISTS":
            dispatch(
              appActions.gotoStep("Error", {
                title: langPack["labelError"],
                content: langPack["DRAFT_ENTRY_ALREADY_EXISTS"],
                button: {
                  text: langPack["buttonStartNew"],
                  onClick: async () => {
                    const response = await api.deleteApplication(userInfo.id);
                    if (response && response.status === 200) nextStepHandler();
                  },
                },
              })
            );
            break;

          case "INVALID_CAPTCHA":
            resetReCaptcha();
            setReCaptchaErrorMsg(langPack["INVALID_CAPTCHA-NEW-SUBMISSION"]);
            setShowReCaptchaErrorMsg(true);
            break;

          case "EMPTY_CAPTCHA":
            resetReCaptcha();
            setReCaptchaErrorMsg(langPack["EMPTY_CAPTCHA-NEW-SUBMISSION"]);
            setShowReCaptchaErrorMsg(true);
            break;

          default:
            break;
        }
      }
    }
  };

  const handleUpdateUserInfo: ITextInputProps["onValidation"] = (
    validation
  ) => {
    const { isValid, inputId, value } = validation;
    if (isValid) {
      switch (inputId) {
        case "email":
          setTooltipOpen(false);
          break;
        case "mobile":
          if (userInfo.activeCountry === "TH") {
            const phoneNumber = /^([\d]{3})([\d]{3})([\d]{4})$/
              .exec(value)
              .slice(1, 4)
              .join("-");
            setForceMobile({ value: phoneNumber });
            return dispatch(
              userInfoActions.updateUserInfo({ [inputId]: value })
            );
          } else {
            const fullNumber = (
              "+" +
              getCountryCallingCode(
                forceCountryCode.value || userInfo.activeCountry
              ) +
              value
            ).replace(/[ -()]/, "");
            const phoneNumber = formatIncompletePhoneNumber(
              value,
              forceCountryCode.value || userInfo.activeCountry
            );
            setForceMobile({ value: phoneNumber });
            return dispatch(
              userInfoActions.updateUserInfo({ [inputId]: fullNumber })
            );
          }
      }
      dispatch(userInfoActions.updateUserInfo({ [inputId]: value }));
    } else {
      switch (inputId) {
        case "email":
          if (!isTooltipOpen) {
            setTooltipOpen(true);
            handleTooltipClose();
          }
          break;
      }
      dispatch(userInfoActions.updateUserInfo({ [inputId]: "" }));
    }
  };

  const handleTooltipOpen = (event: React.MouseEvent) => {
    if (isTooltipOpen) return;
    if (
      (cluster["email"] && !cluster["email"](new Event("change"))) ||
      event.currentTarget.className.includes("app-tooltip__dot")
    ) {
      setTooltipOpen(true);
      handleTooltipClose();
      event.stopPropagation();
    }
  };

  const handleTooltipClose = () => {
    const handleCloseTooltip = () => {
      setTooltipOpen(false);
      window.removeEventListener("click", handleCloseTooltip);
    };
    window.addEventListener("click", handleCloseTooltip);
  };

  const initMobileNumber =
    userInfo.activeCountry !== "TH"
      ? userInfo.mobile
        ? parsePhoneNumberFromString(userInfo.mobile).formatNational()
        : ""
      : /^([\d]{10})$/.test(userInfo.mobile)
        ? /^([\d]{3})([\d]{3})([\d]{4})$/
          .exec(userInfo.mobile)
          .slice(1, 4)
          .join("-")
        : "";

  const handleRecaptcha = (reCaptchaKey: string) => {
    dispatch(
      ReCaptchAction.updateCaptureVerified({
        reCaptchaKey: reCaptchaKey,
        isReCaptchaVerified: true,
      })
    );
    setShowReCaptchaErrorMsg(false);
  };

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const resetReCaptcha = () => {
    dispatch(
      ReCaptchAction.updateCaptureVerified({
        reCaptchaKey: "",
        isReCaptchaVerified: false,
      })
    );
    recaptchaRef.current?.reset();
  };

  return (
    <React.Fragment>
      <div
        className={classNames({
          "content content--form": true,
          "content content--locked": !!userInfo.appId,
        })}
      >
        <div className="basic-info__header" style={{ marginTop: 0 }}>
          <div>
            <h2 className="app-title">{langPack["personalInformation"]}</h2>
            {/* <p className="app-sub-title">
                            {langPack["clickHereForInstructions"]}
                        </p> */}
          </div>
          {languageList && languageList.length > 1 && (
            <div
              style={{ height: 50, maxHeight: 50, marginLeft: 10, zIndex: 10 }}
            >
              <LanguageSelector
                languageList={languageList}
                selected={lang}
                onSelect={(value) => {
                  retrieveLangPack(value, userInfo.activeCountry);
                }}
              />
            </div>
          )}
        </div>

        <TextInput
          inputLabel={langPack["labelFullName"]}
          inputId={"fullName"}
          initValue={userInfo.fullName}
          validators={[isRequired]}
          cluster={cluster}
          onValidation={handleUpdateUserInfo}
          typingHandler={noSymbolFilter}
          disabled={!!userInfo.appId}
        />

        {userInfo.activeCountry === "TH" && (
          <TextInput
            inputLabel={langPack["labelFullNameEn"]}
            inputId={"fullNameEn"}
            initValue={userInfo.fullNameEn}
            validators={[isRequired]}
            cluster={cluster}
            onValidation={handleUpdateUserInfo}
            typingHandler={noSymbolFilter}
            disabled={!!userInfo.appId}
          />
        )}
        {isDesktopView ? (
          // <DesktopDateOfBirthSelect
          //     cluster={cluster}
          //     disabled={!!userInfo.appId}
          //     initValue={userInfo.dateOfBirth}
          // />
          <DateOfBirthFieldDesktop
            cluster={cluster}
            disabled={!!userInfo.appId}
          />
        ) : (
          <DateOfBirthField cluster={cluster} disabled={!!userInfo.appId} />
        )}
        {userInfo.activeCountry === "TH" && (
          <DropdownSelect
            inputLabel={langPack["labelGender"]}
            initValue={userInfo.gender}
            inputId="gender"
            dropdownItemList={genderOptions}
            errorMessage=""
            onSelect={(selection) =>
              dispatch(
                userInfoActions.updateUserInfo({
                  [selection.inputId]: selection.value,
                })
              )
            }
            cluster={cluster}
            disabled={!!userInfo.appId}
          />
        )}

        {userInfo.activeCountry === "SG" ? (
          <TextInput // SG ID
            inputLabel={langPack["labelIdentityNoSG"]}
            initValue={userInfo.id}
            inputId={"id"}
            upperCase
            maxLength={5}
            validators={[isRequired, onlyAlphanumeric, checkShortSgId]}
            cluster={cluster}
            onValidation={handleUpdateUserInfo}
            disabled={!!userInfo.appId}
          />
        ) : (
          <TextInput // Other Countries ID
            inputLabel={langPack["labelIdentityNoOther"]}
            initValue={userInfo.id}
            inputId={"id"}
            upperCase
            validators={[
              isRequired,
              onlyAlphanumeric,
              checkSize(10),
            ]}
            cluster={cluster}
            onValidation={handleUpdateUserInfo}
            maxLength={10}
            disabled={!!userInfo.appId}
          />
        )}
        <DropdownSelect
          inputLabel={langPack["labelCitizenship"]}
          initValue={userInfo.citizenship}
          inputId="citizenship"
          dropdownItemList={citizenshipList}
          errorMessage=""
          onSelect={(selection) =>
            dispatch(
              userInfoActions.updateUserInfo({
                [selection.inputId]: selection.value,
              })
            )
          }
          cluster={cluster}
          disabled={!!userInfo.appId}
        />
        {userInfo.activeCountry === "TH" ? (
          <TextInput
            inputLabel={langPack["labelMobile"]}
            initValue={initMobileNumber}
            forceUpdate={forceMobile}
            inputId={"mobile"}
            validators={[isRequired, isThNumber]}
            cluster={cluster}
            onValidation={handleUpdateUserInfo}
            typingHandler={digitsOnlyFilter}
            type={"tel"}
            inputMode={"tel"}
            disabled={!!userInfo.appId}
          />
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <DropdownSelect
              style={{ width: 110 }}
              inputLabel=""
              inputId="mobileCountryCode"
              initValue={forceCountryCode.value}
              forceUpdate={forceCountryCode}
              dropdownItemList={countryCodeList}
              errorMessage=""
              onSelect={(selection) => {
                setForceCountryCode({ value: selection.value });
              }}
              cluster={cluster}
              enableSearch
              selectedItemRenderer={countrySelected}
              listItemRenderer={countryOption}
              disabled={!!userInfo.appId}
            />
            <TextInput
              style={{ width: "calc(100% - 130px)" }}
              inputLabel={langPack["labelMobile"]}
              initValue={initMobileNumber}
              forceUpdate={forceMobile}
              inputId={"mobile"}
              validators={
                forceCountryCode.value === "SG"
                  ? [
                    isRequired,
                    validatePhoneNumber(
                      forceCountryCode.value || userInfo.activeCountry
                    ),
                    validateSgNumber,
                  ]
                  : [
                    isRequired,
                    validatePhoneNumber(
                      forceCountryCode.value || userInfo.activeCountry
                    ),
                  ]
              }
              cluster={cluster}
              onValidation={handleUpdateUserInfo}
              typingHandler={digitsOnlyFilter}
              type={"tel"}
              inputMode={"tel"}
              disabled={!!userInfo.appId}
            />
          </div>
        )}
        <div
          className="email-wrapper"
          style={{ position: "relative" }}
          onClick={handleTooltipOpen}
        >
          <TextInput
            inputLabel={langPack["labelEmail"]}
            initValue={userInfo.email}
            inputId={"email"}
            validators={[isRequired, checkEmail]}
            cluster={cluster}
            onValidation={handleUpdateUserInfo}
            disabled={!!userInfo.appId}
          />
          <Tooltip
            text={langPack["emailWarning"]}
            open={isTooltipOpen}
            style={{ bottom: 24 }}
            onClick={handleTooltipOpen}
          />
        </div>
        {isCaptcha && !userInfo.appId && (
          <div
            className={showReCaptchaErrorMsg ? "ReCaptcha-Div" : ""}
          >
            <ReCAPTCHA
              className={showReCaptchaErrorMsg ? "ReCaptcha-error-border" : ""}
              ref={recaptchaRef}
              sitekey={clientSideKey}
              onChange={handleRecaptcha}
            />
            {showReCaptchaErrorMsg && (
              <div
                className={"ReCaptcha-error-message"}
              >
                {ReCaptchaErrorMsg}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="action">
        {langPack["competitionDisclaimer"] && (
          <>
            <p className="app-disclaimer">
              {langPack["competitionDisclaimerText"]}
            </p>
            <br />
          </>
        )}
        <PrimaryButton
          text={langPack["buttonNext"]}
          onClick={nextStepHandler}
          disabled={isCallingApi}
        />
      </div>
    </React.Fragment>
  );
};

export default BasicInfo;
